import { notification, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { FinetuningService } from "../../../services/FinetuningService";
import { FinetuneRunNew } from "../../../model_gen/finetuning";

export const NEW_FINETUNE_STORAGE_KEY = "finetuneData";

export const getStatusColor = (status: string) => {
  const statusMap: Record<string, string> = {
    CREATED: "default",
    PENDING: "processing",
    RUNNING: "processing",
    COMPLETED: "success",
    FAILED: "error",
    CANCELLED: "warning",
  };
  return statusMap[status] || "default";
};

export const handleRetryRun = (
  run: FinetuneRunNew,
  setIsLoading: (loading: boolean) => void,
  apiKey: string,
  onSuccess?: () => void
) => {
  FinetuneRunService.retryFinetuneRun(
    run.id,
    false,
    setIsLoading,
    apiKey
  ).subscribe(
    () => {
      notification.success({
        message: "Success",
        description: "Finetune run retried successfully",
      });
      onSuccess?.();
    },
    (error: any) => {
      console.error(error);
      notification.error({
        message: "Error",
        description: "Failed to retry finetune run",
      });
    }
  );
};

export const handleDeleteRun = (
  run: FinetuneRunNew,
  setIsLoading: (loading: boolean) => void,
  apiKey: string,
  onSuccess?: () => void
) => {
  Modal.confirm({
    title: "Delete Finetune Run",
    icon: <ExclamationCircleOutlined />,
    content: `Are you sure you want to delete "${run.display_name}"? This action cannot be undone.`,
    okText: "Delete",
    okType: "danger",
    cancelText: "Cancel",
    onOk: () => {
      FinetuningService.updateFinetuneStatus(
        run.id.toString(),
        "DELETED",
        setIsLoading,
        apiKey
      ).subscribe(
        () => {
          notification.success({
            message: "Success",
            description: "Finetune run deleted successfully",
          });
          onSuccess?.();
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to delete finetune run",
          });
        }
      );
    },
  });
};

export const handleCancelRun = (
  run: FinetuneRunNew,
  setIsLoading: (loading: boolean) => void,
  apiKey: string,
  onSuccess?: () => void
) => {
  Modal.confirm({
    title: "Cancel Finetune Run",
    icon: <ExclamationCircleOutlined />,
    content: `Are you sure you want to cancel "${run.display_name}"?`,
    okText: "Cancel Run",
    okType: "danger",
    cancelText: "No",
    onOk: () => {
      FinetuneRunService.cancelFinetuneRun(
        run.id,
        setIsLoading,
        apiKey
      ).subscribe(
        () => {
          notification.success({
            message: "Success",
            description: "Finetune run cancelled successfully",
          });
          onSuccess?.();
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: "Failed to cancel finetune run",
          });
        }
      );
    },
  });
};

export const handleRerunFinetune = (
  run: FinetuneRunNew,
  datasets: { [key: number]: any },
  navigate: ReturnType<typeof useNavigate>
) => {
  // Create a copy of the finetune data
  const finetuneData = {
    dataset_id: run.finetune_dataset_id,
    application_id: run.application_id,
    intent_id: run.intent_id,
    intent_group_id: run.intent_group_id,
    prompt: run.prompt,
    fourbit: run.fourbit,
    base_model: run.base_model,
    display_name: `${run.display_name} (copy)`,
    description: run.description
      ? `Copy of ${run.display_name}\n\n${run.description}`
      : `Copy of ${run.display_name}`,
    prompt_deletions: run.prompt_deletions || [],
    warmup_steps: run.meta?.warmup_steps,
    warmup_ratio: run.meta?.warmup_ratio,
    learning_rate: run.meta?.learning_rate,
    per_device_train_batch_size: run.meta?.per_device_train_batch_size,
    gradient_accumulation_steps: run.meta?.gradient_accumulation_steps,
    train_on_responses_only: run.meta?.train_on_responses_only,
    train_split: run.meta?.train_split,
    alpha: run.meta?.alpha,
    rank: run.meta?.rank,
    epochs: run.meta?.num_train_epochs,
    weight_decay: run.meta?.weight_decay,
    dropout: run.meta?.dropout,
    rslora: run.meta?.rslora,
    selected_sub_dataset: run.meta?.sub_dataset_id
      ? datasets[run.meta.sub_dataset_id]
      : null,
  };

  // Save to localStorage
  localStorage.setItem(NEW_FINETUNE_STORAGE_KEY, JSON.stringify(finetuneData));

  // Navigate to new finetune run page, starting at step 2 (Finetune Details)
  navigate("/forge/finetuning/new?step=2");
};
